// Initialises accordion blocks within the paint-selector section.
// theme.js only wires up accordions inside the registered "product" section;
// this file replicates that behaviour using the same slide-anim helpers
// extracted from theme.js (slide-anim MIT © 2017 @yomotsu).

(function () {
  // --- slide-anim helpers (extracted from theme.js) ---

  const pool = [];
  const inAnimItems = {
    add(el, defaultStyle, timeoutId, onCancelled) {
      const inAnimItem = { el, defaultStyle, timeoutId, onCancelled };
      this.remove(el);
      pool.push(inAnimItem);
    },
    remove(el) {
      const index = inAnimItems.findIndex(el);
      if (index === -1) return;
      const inAnimItem = pool[index];
      clearTimeout(inAnimItem.timeoutId);
      inAnimItem.onCancelled();
      pool.splice(index, 1);
    },
    find(el) {
      return pool[inAnimItems.findIndex(el)];
    },
    findIndex(el) {
      let index = -1;
      pool.some((item, i) => {
        if (item.el === el) { index = i; return true; }
        return false;
      });
      return index;
    }
  };

  const CSS_EASEOUT_EXPO = 'cubic-bezier( 0.19, 1, 0.22, 1 )';

  function isVisible(el) {
    return el.offsetHeight !== 0;
  }

  function pxToNumber(px) {
    return +px.replace(/px/, '');
  }

  function resetStyle(el) {
    el.style.visibility = '';
    el.style.height = '';
    el.style.minHeight = '';
    el.style.paddingTop = '';
    el.style.paddingBottom = '';
    el.style.borderTopWidth = '';
    el.style.borderBottomWidth = '';
    el.style.overflow = '';
    el.style.transition = '';
    el.style.webkitTransition = '';
  }

  function getDefaultStyles(el, defaultDisplay = 'block') {
    const defaultStyle = el.getAttribute('style') || '';
    const style = window.getComputedStyle(el);
    el.style.visibility = 'hidden';
    el.style.display = defaultDisplay;
    const width = pxToNumber(style.getPropertyValue('width'));
    el.style.position = 'absolute';
    el.style.width = `${width}px`;
    el.style.height = '';
    el.style.minHeight = '';
    el.style.paddingTop = '';
    el.style.paddingBottom = '';
    el.style.borderTopWidth = '';
    el.style.borderBottomWidth = '';
    const minHeight = pxToNumber(style.getPropertyValue('min-height'));
    const paddingTop = pxToNumber(style.getPropertyValue('padding-top'));
    const paddingBottom = pxToNumber(style.getPropertyValue('padding-bottom'));
    const borderTop = pxToNumber(style.getPropertyValue('border-top-width'));
    const borderBottom = pxToNumber(style.getPropertyValue('border-bottom-width'));
    const height = el.scrollHeight;
    el.setAttribute('style', defaultStyle);
    return { height, minHeight, paddingTop, paddingBottom, borderTop, borderBottom };
  }

  function slideDown(el, options = {}) {
    return new Promise((resolve) => {
      if (inAnimItems.findIndex(el) !== -1) return;
      const _isVisible = isVisible(el);
      const hasEndHeight = typeof options.endHeight === 'number';
      const display = options.display || 'block';
      const duration = options.duration || 400;
      const onCancelled = options.onCancelled || function () {};
      const defaultStyle = el.getAttribute('style') || '';
      const style = window.getComputedStyle(el);
      const defaultStyles = getDefaultStyles(el, display);
      const isBorderBox = /border-box/.test(style.getPropertyValue('box-sizing'));
      const contentHeight = defaultStyles.height;
      const minHeight = defaultStyles.minHeight;
      const paddingTop = defaultStyles.paddingTop;
      const paddingBottom = defaultStyles.paddingBottom;
      const borderTop = defaultStyles.borderTop;
      const borderBottom = defaultStyles.borderBottom;
      const cssDuration = `${duration}ms`;
      const cssTransition = [
        `height ${cssDuration} ${CSS_EASEOUT_EXPO}`,
        `min-height ${cssDuration} ${CSS_EASEOUT_EXPO}`,
        `padding ${cssDuration} ${CSS_EASEOUT_EXPO}`,
        `border-width ${cssDuration} ${CSS_EASEOUT_EXPO}`
      ].join();
      const startHeight = _isVisible ? style.height : '0px';
      const startMinHeight = _isVisible ? style.minHeight : '0px';
      const startPaddingTop = _isVisible ? style.paddingTop : '0px';
      const startPaddingBottom = _isVisible ? style.paddingBottom : '0px';
      const startBorderTopWidth = _isVisible ? style.borderTopWidth : '0px';
      const startBorderBottomWidth = _isVisible ? style.borderBottomWidth : '0px';
      const endHeight = hasEndHeight
        ? `${options.endHeight}px`
        : !isBorderBox
          ? `${contentHeight - paddingTop - paddingBottom}px`
          : `${contentHeight + borderTop + borderBottom}px`;
      if (
        startHeight === endHeight &&
        startPaddingTop === `${paddingTop}px` &&
        startPaddingBottom === `${paddingBottom}px` &&
        startBorderTopWidth === `${borderTop}px` &&
        startBorderBottomWidth === `${borderBottom}px`
      ) { resolve(); return; }
      requestAnimationFrame(() => {
        el.style.height = startHeight;
        el.style.minHeight = startMinHeight;
        el.style.paddingTop = startPaddingTop;
        el.style.paddingBottom = startPaddingBottom;
        el.style.borderTopWidth = startBorderTopWidth;
        el.style.borderBottomWidth = startBorderBottomWidth;
        el.style.display = display;
        el.style.overflow = 'hidden';
        el.style.visibility = 'visible';
        el.style.transition = cssTransition;
        el.style.webkitTransition = cssTransition;
        requestAnimationFrame(() => {
          el.style.height = endHeight;
          el.style.minHeight = `${minHeight}px`;
          el.style.paddingTop = `${paddingTop}px`;
          el.style.paddingBottom = `${paddingBottom}px`;
          el.style.borderTopWidth = `${borderTop}px`;
          el.style.borderBottomWidth = `${borderBottom}px`;
        });
      });
      const timeoutId = setTimeout(() => {
        resetStyle(el);
        el.style.display = display;
        if (hasEndHeight) { el.style.height = `${options.endHeight}px`; el.style.overflow = 'hidden'; }
        inAnimItems.remove(el);
        resolve();
      }, duration);
      inAnimItems.add(el, defaultStyle, timeoutId, onCancelled);
    });
  }

  function slideUp(el, options = {}) {
    return new Promise((resolve) => {
      if (inAnimItems.findIndex(el) !== -1) return;
      const _isVisible = isVisible(el);
      const duration = options.duration || 400;
      const onCancelled = options.onCancelled || function () {};
      if (!_isVisible) { resolve(); return; }
      const defaultStyle = el.getAttribute('style') || '';
      const style = window.getComputedStyle(el);
      const isBorderBox = /border-box/.test(style.getPropertyValue('box-sizing'));
      const minHeight = pxToNumber(style.getPropertyValue('min-height'));
      const paddingTop = pxToNumber(style.getPropertyValue('padding-top'));
      const paddingBottom = pxToNumber(style.getPropertyValue('padding-bottom'));
      const borderTop = pxToNumber(style.getPropertyValue('border-top-width'));
      const borderBottom = pxToNumber(style.getPropertyValue('border-bottom-width'));
      const contentHeight = el.scrollHeight;
      const cssDuration = duration + 'ms';
      const cssTransition = [
        `height ${cssDuration} ${CSS_EASEOUT_EXPO}`,
        `padding ${cssDuration} ${CSS_EASEOUT_EXPO}`,
        `border-width ${cssDuration} ${CSS_EASEOUT_EXPO}`
      ].join();
      const startHeight = !isBorderBox
        ? `${contentHeight - paddingTop - paddingBottom}px`
        : `${contentHeight + borderTop + borderBottom}px`;
      requestAnimationFrame(() => {
        el.style.height = startHeight;
        el.style.minHeight = `${minHeight}px`;
        el.style.paddingTop = `${paddingTop}px`;
        el.style.paddingBottom = `${paddingBottom}px`;
        el.style.borderTopWidth = `${borderTop}px`;
        el.style.borderBottomWidth = `${borderBottom}px`;
        el.style.display = 'block';
        el.style.overflow = 'hidden';
        el.style.transition = cssTransition;
        el.style.webkitTransition = cssTransition;
        requestAnimationFrame(() => {
          el.style.height = '0';
          el.style.minHeight = '0';
          el.style.paddingTop = '0';
          el.style.paddingBottom = '0';
          el.style.borderTopWidth = '0';
          el.style.borderBottomWidth = '0';
        });
      });
      const timeoutId = setTimeout(() => {
        resetStyle(el);
        el.style.display = 'none';
        inAnimItems.remove(el);
        resolve();
      }, duration);
      inAnimItems.add(el, defaultStyle, timeoutId, onCancelled);
    });
  }

  function slideStop(el) {
    const elementObject = inAnimItems.find(el);
    if (!elementObject) return;
    const style = window.getComputedStyle(el);
    const height = style.height;
    const paddingTop = style.paddingTop;
    const paddingBottom = style.paddingBottom;
    const borderTopWidth = style.borderTopWidth;
    const borderBottomWidth = style.borderBottomWidth;
    resetStyle(el);
    el.style.height = height;
    el.style.paddingTop = paddingTop;
    el.style.paddingBottom = paddingBottom;
    el.style.borderTopWidth = borderTopWidth;
    el.style.borderBottomWidth = borderBottomWidth;
    el.style.overflow = 'hidden';
    inAnimItems.remove(el);
  }

  // --- Accordion initialiser (mirrors theme.js accordion()) ---

  function initAccordion(node) {
    const labels = Array.from(node.querySelectorAll('.accordion__label'));

    labels.forEach(label => {
      label.addEventListener('click', e => {
        e.preventDefault();
        const group = label.parentNode;
        const content = label.nextElementSibling;
        slideStop(content);
        if (isVisible(content)) {
          slideUp(content);
          group.setAttribute('data-open', 'false');
          label.setAttribute('aria-expanded', 'false');
          content.setAttribute('aria-hidden', 'true');
        } else {
          slideDown(content);
          group.setAttribute('data-open', 'true');
          label.setAttribute('aria-expanded', 'true');
          content.setAttribute('aria-hidden', 'false');
        }
      });
    });
  }

  // --- Quick-view button initialiser (mirrors theme.js ProductItem quick-view handler) ---
  // Uses window.emit exposed by theme.js to fire the quick-view:open event that the
  // existing quick-view modal listens for.

  function initQuickViewButtons(section) {
    const buttons = Array.from(section.querySelectorAll('.show-product-quick-view'));

    buttons.forEach(button => {
      button.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        const productEl = button.closest('.product-item');
        if (!productEl) return;
        const url    = productEl.dataset.url;
        const title  = productEl.dataset.title;
        const media  = productEl.querySelector('.product-item__image-link');
        const badges = productEl.querySelector('.product-badges');
        window.emit('quick-view:open', null, {
          type: 'quick-view',
          productUrl: url,
          productMedia: media,
          productBadges: badges,
          productTitle: title
        });
      });
    });
  }

  const section = document.querySelector('[data-section-type="paint-selector"]');
  if (section) {
    section.querySelectorAll('.accordion').forEach(node => initAccordion(node));
    initQuickViewButtons(section);
  }
})();


// List / Row view switcher
(function () {
  function switchLayout (option) {
    const list = document.querySelector('.paint-selector__products-list');

    if (option === 'rows') {
      localStorage.setItem('paint-selector-layout', 'rows');
      list.classList.add('list--row');
    } else {
      localStorage.removeItem('paint-selector-layout');
      list.classList.remove('list--row');
    }

    document.querySelectorAll('.paint-selector__layouts button')
      .forEach(btn2 => {
        if (btn2.dataset.layout === option) {
          btn2.classList.add('active');
        } else {
          btn2.classList.remove('active');
        }
      });
  }

  document.querySelectorAll('.paint-selector__layouts button')
    .forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const btn = e.target.closest('button');

        if (btn.classList.contains('active')) return;

        const option = btn.dataset.layout;
        switchLayout(option)
      });
    });

  if (localStorage.getItem('paint-selector-layout') === 'rows') {
    switchLayout('rows');
  }
})();


// Paint Selector — selection, drawer, and add-to-cart
(function () {
  var STORAGE_KEY = 'paint-selector-selection';

  // ─── Storage ──────────────────────────────────────────────────────────────

  function loadSelection() {
    try {
      return migrate(JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'));
    } catch (_) {
      return [];
    }
  }

  // Upgrades old format (flat repeated entries) to grouped entries with quantity.
  function migrate(raw) {
    var result = [];
    raw.forEach(function (item) {
      var existing = result.find(function (r) {
        return r.productId === item.productId && r.variantId === item.variantId;
      });
      if (existing) {
        existing.quantity = (existing.quantity || 1) + 1;
      } else {
        result.push({
          productId:      item.productId      || '',
          variantId:      item.variantId      || '',
          title:          item.title          || '',
          price:          item.price          || 0,
          compareAtPrice: item.compareAtPrice || 0,
          image:          item.image          || '',
          url:            item.url            || '',
          quantity:       item.quantity       || 1,
          variants:       item.variants       || []
        });
      }
    });
    return result;
  }

  function saveSelection(selection) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  function fmt(cents) {
    return '£' + (cents / 100).toFixed(2);
  }

  function esc(str) {
    return String(str).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function totalCount(sel) {
    return sel.reduce(function (s, i) { return s + (i.quantity || 1); }, 0);
  }

  function totalPrice(sel) {
    return sel.reduce(function (s, i) { return s + i.price * (i.quantity || 1); }, 0);
  }

  function countForProduct(sel, productId) {
    return sel.reduce(function (s, i) {
      return s + (i.productId === productId ? (i.quantity || 1) : 0);
    }, 0);
  }

  // ─── DOM reading ─────────────────────────────────────────────────────────

  function getVariantData(productItem) {
    var addDiv = productItem.querySelector('.product-item__paint-selector-add');
    var radios  = productItem.querySelectorAll('.product-item__paint-selector__variants input[type="radio"]');

    if (radios.length === 0) {
      return {
        variantId:      productItem.dataset.variantId || '',
        price:          parseInt((addDiv && addDiv.dataset.defaultPrice)          || '0', 10) || 0,
        compareAtPrice: parseInt((addDiv && addDiv.dataset.defaultCompareAtPrice) || '0', 10) || 0
      };
    }

    var checked = productItem.querySelector('.product-item__paint-selector__variants input[type="radio"]:checked');
    if (!checked) return null;

    return {
      variantId:      checked.value,
      price:          parseInt(checked.dataset.price          || '0', 10),
      compareAtPrice: parseInt(checked.dataset.compareAtPrice || '0', 10)
    };
  }

  function buildVariantsFromDom(productItem) {
    var radios = productItem.querySelectorAll('.product-item__paint-selector__variants input[type="radio"]');
    if (radios.length === 0) {
      var addDiv = productItem.querySelector('.product-item__paint-selector-add');
      return [{
        id:             productItem.dataset.variantId || '',
        title:          '',
        price:          parseInt((addDiv && addDiv.dataset.defaultPrice)          || '0', 10) || 0,
        compareAtPrice: parseInt((addDiv && addDiv.dataset.defaultCompareAtPrice) || '0', 10) || 0
      }];
    }
    return Array.from(radios).map(function (r) {
      return {
        id:             r.value,
        title:          r.dataset.title || '',
        price:          parseInt(r.dataset.price          || '0', 10),
        compareAtPrice: parseInt(r.dataset.compareAtPrice || '0', 10)
      };
    });
  }

  // ─── Selection mutations ──────────────────────────────────────────────────

  function addToSelection(productItem) {
    var variant = getVariantData(productItem);
    if (!variant) return;

    var productId = productItem.dataset.id;
    var sel       = loadSelection();
    var existing  = sel.find(function (i) {
      return i.productId === productId && i.variantId === variant.variantId;
    });

    if (existing) {
      existing.quantity += 1;
    } else {
      sel.push({
        productId:      productId,
        variantId:      variant.variantId,
        title:          productItem.dataset.title          || '',
        price:          variant.price,
        compareAtPrice: variant.compareAtPrice,
        image:          productItem.dataset.featuredImage  || '',
        url:            productItem.dataset.url            || '',
        quantity:       1,
        variants:       buildVariantsFromDom(productItem)
      });
    }

    saveSelection(sel);
    updateUI();
  }

  function removeFromSelection(index) {
    var sel = loadSelection();
    sel.splice(index, 1);
    saveSelection(sel);
    updateUI();
  }

  function changeQuantity(index, delta) {
    var sel = loadSelection();
    if (!sel[index]) return;
    sel[index].quantity = (sel[index].quantity || 1) + delta;
    if (sel[index].quantity <= 0) sel.splice(index, 1);
    saveSelection(sel);
    updateUI();
  }

  function changeQuantityForProduct(productItem, delta) {
    var variant = getVariantData(productItem);
    if (!variant) return;

    var productId = productItem.dataset.id;
    var sel       = loadSelection();
    var index     = sel.findIndex(function (i) {
      return i.productId === productId && i.variantId === variant.variantId;
    });

    if (index === -1) {
      if (delta > 0) addToSelection(productItem);
      return;
    }

    changeQuantity(index, delta);
  }

  function changeVariant(index, newVariantId) {
    var sel  = loadSelection();
    var item = sel[index];
    if (!item) return;

    var newV = (item.variants || []).find(function (v) { return v.id === newVariantId; });
    if (!newV) return;

    // If the chosen variant already has its own entry, merge quantities instead.
    var mergeIdx = sel.findIndex(function (s, i) {
      return i !== index && s.productId === item.productId && s.variantId === newVariantId;
    });

    if (mergeIdx >= 0) {
      sel[mergeIdx].quantity += item.quantity;
      sel.splice(index, 1);
    } else {
      item.variantId      = newVariantId;
      item.price          = newV.price;
      item.compareAtPrice = newV.compareAtPrice;
    }

    saveSelection(sel);
    updateUI();
  }

  // ─── UI ───────────────────────────────────────────────────────────────────

  var stickySubmitIsVisible = true;

  function updateStickyBarVisibility() {
    var bar = document.querySelector('.paint-selector-sticky-bar');
    if (!bar) return;
    var shouldShow = totalCount(loadSelection()) > 0 && !stickySubmitIsVisible;
    if (shouldShow) {
      bar.classList.add('is-visible');
      bar.setAttribute('aria-hidden', 'false');
    } else {
      bar.classList.remove('is-visible');
      bar.setAttribute('aria-hidden', 'true');
    }
  }

  function updateUI() {
    var sel   = loadSelection();
    var count = totalCount(sel);
    var price = totalPrice(sel);

    var submitBtn = document.querySelector('.paint-selector__submit');
    if (submitBtn) {
      if (count > 0) {
        submitBtn.classList.add('btn--primary');
        submitBtn.classList.remove('btn--tertiary');

        submitBtn.innerHTML = '<svg style="margin-right: .5rem;" width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
          '<path d="M0.333333 15.6667H13.6667C13.7667 15.6667 13.8333 15.6333 13.9 15.5667C13.9667 15.5 14 15.4 14 15.3L13 3.3C13 3.13333 12.8333 3 12.6667 3H10.0667L10.6667 2.13333C10.7333 2.06667 10.7333 1.96667 10.7333 1.9C10.7333 1.83333 10.6667 1.73333 10.6 1.7L8.23333 0.0666666C8.16667 -6.45717e-08 8.06667 0 8 0C7.9 0 7.83333 0.0666665 7.8 0.133333L5.83333 3H1.33333C1.16667 3 1 3.13333 1 3.3L0 15.3C0 15.4 0.0333334 15.5 0.1 15.5667C0.166667 15.6333 0.233333 15.6667 0.333333 15.6667ZM8.1 0.8L9.93333 2.06667L9.26667 3.03333H6.56667L8.1 0.8ZM1.63333 3.66667H12.3667L13.3 15H0.7L1.63333 3.66667Z" fill="white"/>\n' +
          '<path d="M10.7336 5.46655H8.83359C8.63359 5.46655 8.50026 5.59989 8.50026 5.79989C8.50026 5.99989 8.63359 6.13322 8.83359 6.13322H9.43359V7.23322C9.43359 8.56655 8.33359 9.66655 7.00026 9.66655C5.66693 9.66655 4.56693 8.56655 4.56693 7.23322V6.13322H5.16693C5.36693 6.13322 5.50026 5.99989 5.50026 5.79989C5.50026 5.59989 5.36693 5.46655 5.16693 5.46655H3.26693C3.06693 5.46655 2.93359 5.59989 2.93359 5.79989C2.93359 5.99989 3.06693 6.13322 3.26693 6.13322H3.86693V7.23322C3.86693 8.93322 5.26693 10.3332 6.96693 10.3332C8.66693 10.3332 10.0669 8.93322 10.0669 7.23322V6.13322H10.6669C10.8669 6.13322 11.0003 5.99989 11.0003 5.79989C11.0003 5.59989 10.9003 5.46655 10.7336 5.46655Z" fill="white"/>\n' +
          '</svg>' +
          'Add to BAG (' + count + ') | ' + fmt(price);
      } else {
        submitBtn.classList.add('btn--tertiary');
        submitBtn.classList.remove('btn--primary');
        submitBtn.innerHTML = 'Select options';
      }
    }

    var viewBtn = document.querySelector('.paint-selector__view-selection');
    if (viewBtn) {
      var countEl = viewBtn.querySelector('.paint-selector__view-selection-count');
      viewBtn.style.display = count > 0 ? '' : 'none';
      if (countEl) countEl.textContent = count;
    }

    var drawerCount = document.querySelector('.colours-count');
    if (drawerCount) drawerCount.textContent = count;

    var checkoutBtn = document.querySelector('.paint-selection-drawer__checkout');
    if (checkoutBtn) {
      checkoutBtn.innerHTML = count > 0
        ? '<svg style="margin-right: .5rem;" width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
        '<path d="M0.333333 15.6667H13.6667C13.7667 15.6667 13.8333 15.6333 13.9 15.5667C13.9667 15.5 14 15.4 14 15.3L13 3.3C13 3.13333 12.8333 3 12.6667 3H10.0667L10.6667 2.13333C10.7333 2.06667 10.7333 1.96667 10.7333 1.9C10.7333 1.83333 10.6667 1.73333 10.6 1.7L8.23333 0.0666666C8.16667 -6.45717e-08 8.06667 0 8 0C7.9 0 7.83333 0.0666665 7.8 0.133333L5.83333 3H1.33333C1.16667 3 1 3.13333 1 3.3L0 15.3C0 15.4 0.0333334 15.5 0.1 15.5667C0.166667 15.6333 0.233333 15.6667 0.333333 15.6667ZM8.1 0.8L9.93333 2.06667L9.26667 3.03333H6.56667L8.1 0.8ZM1.63333 3.66667H12.3667L13.3 15H0.7L1.63333 3.66667Z" fill="white"/>\n' +
        '<path d="M10.7336 5.46655H8.83359C8.63359 5.46655 8.50026 5.59989 8.50026 5.79989C8.50026 5.99989 8.63359 6.13322 8.83359 6.13322H9.43359V7.23322C9.43359 8.56655 8.33359 9.66655 7.00026 9.66655C5.66693 9.66655 4.56693 8.56655 4.56693 7.23322V6.13322H5.16693C5.36693 6.13322 5.50026 5.99989 5.50026 5.79989C5.50026 5.59989 5.36693 5.46655 5.16693 5.46655H3.26693C3.06693 5.46655 2.93359 5.59989 2.93359 5.79989C2.93359 5.99989 3.06693 6.13322 3.26693 6.13322H3.86693V7.23322C3.86693 8.93322 5.26693 10.3332 6.96693 10.3332C8.66693 10.3332 10.0669 8.93322 10.0669 7.23322V6.13322H10.6669C10.8669 6.13322 11.0003 5.99989 11.0003 5.79989C11.0003 5.59989 10.9003 5.46655 10.7336 5.46655Z" fill="white"/>\n' +
        '</svg>' +
        'Add to Bag (' + count + ') | ' + fmt(price)
        : 'Add to Bag';
    }

    var subtotal = document.querySelector('.js-subtotal');
    if (subtotal) subtotal.textContent = count > 0 ? fmt(price) : '';

    document.querySelectorAll('.paint-selector__products-list .product-item').forEach(function (item) {
      var n = countForProduct(sel, item.dataset.id);

      if (n > 0) {
        item.classList.add('paint-selector-item--selected');
      } else {
        item.classList.remove('paint-selector-item--selected');
      }

      var addWrapper = item.querySelector('.product-item__paint-selector-add');
      if (addWrapper) {
        addWrapper.dataset.quantity = n;

        var qtyValue = addWrapper.querySelector('[data-paint-selector-qty-value]');
        if (qtyValue) qtyValue.textContent = n;
      }
    });

    var drawer = document.getElementById('paint-selection-drawer');
    if (drawer && drawer.getAttribute('aria-hidden') === 'false') {
      renderDrawerItems(sel);
    }

    // Sticky bar
    var stickyBar = document.querySelector('.paint-selector-sticky-bar');
    if (stickyBar) {
      var stickyImg   = stickyBar.querySelector('.paint-selector-sticky-bar__img');
      var stickyTotal = stickyBar.querySelector('.paint-selector-sticky-bar__total');
      var stickyBtn   = stickyBar.querySelector('.paint-selector-sticky-bar__btn');

      if (stickyImg) {
        var firstImage = sel.length > 0 ? sel[0].image : '';
        if (firstImage) {
          stickyImg.src    = firstImage;
          stickyImg.hidden = false;
        } else {
          stickyImg.hidden = true;
        }
      }
      if (stickyTotal) stickyTotal.textContent = count > 0 ? fmt(price) : '';
      if (stickyBtn) {
        stickyBtn.textContent = count > 0
          ? 'ADD TO BAG (' + count + ') | ' + fmt(price)
          : 'ADD TO BAG';
      }
      updateStickyBarVisibility();
    }
  }

  var QUICK_VIEW_ICON = '<svg width="16" height="16" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M14 5C8 5 2.73 9.11 1 14c1.73 4.89 7 9 13 9s11.27-4.11 13-9c-1.73-4.89-7-9-13-9zm0 15a6 6 0 110-12 6 6 0 010 12zm0-10a4 4 0 100 8 4 4 0 000-8z" fill="currentColor"/></svg>';

  function renderDrawerItems(sel) {
    var container = document.getElementById('paint-selection-drawer-items');
    if (!container) return;

    if (sel.length === 0) {
      container.innerHTML = '<p class="paint-selection-drawer__empty">No colours selected yet.</p>';
      return;
    }

    container.innerHTML = sel.map(function (item, index) {
      var hasMultiVariant = item.variants && item.variants.length > 1;
      var onSale          = item.compareAtPrice && item.compareAtPrice > item.price;

      var quickViewBtn = item.url
        ? '<button class="btn btn--rounded product-item__disc-button paint-selection-drawer__quick-view" data-url="' + esc(item.url) + '" data-title="' + esc(item.title) + '" aria-label="Quick view ' + esc(item.title) + '">' + QUICK_VIEW_ICON + '</button>'
        : '';

      var imageHtml = item.image
        ? '<div class="paint-selection-drawer__item-img-wrap">' +
            '<img src="' + item.image + '" alt="" width="72" height="72" loading="lazy">' +
          '</div>'
        : '';

      var variantHtml = hasMultiVariant
        ? '<div class="product-item__paint-selector__variants paint-selection-drawer__variants">' +
            item.variants.map(function (v) {
              var uid = 'drawer-' + index + '-' + v.id;
              return '<div>' +
                '<input type="radio"' +
                ' id="' + uid + '"' +
                ' name="drawer-variant-' + index + '"' +
                ' value="' + v.id + '"' +
                ' data-index="' + index + '"' +
                (v.id === item.variantId ? ' checked' : '') + ' />' +
                '<label for="' + uid + '">' + esc(v.title) + '</label>' +
                '</div>';
            }).join('') +
          '</div>'
        : (item.variants && item.variants.length === 1 && item.variants[0].title
            ? '<span class="paint-selection-drawer__item-variant">' + esc(item.variants[0].title) + '</span>'
            : '');

      var priceHtml = '<div class="paint-selection-drawer__item-prices">' +
        '<span class="paint-selection-drawer__price' + (onSale ? ' is-sale' : '') + '">' + fmt(item.price) + '</span>' +
        (onSale ? '<s class="paint-selection-drawer__compare-at">' + fmt(item.compareAtPrice) + '</s>' : '') +
        '</div>';

      return (
        '<div class="paint-selection-drawer__item">' +
          imageHtml +
          '<div class="paint-selection-drawer__item-body">' +
            '<div class="paint-selection-drawer__item-title-row">' +
              '<span class="paint-selection-drawer__item-title">' + esc(item.title) + '</span>' +
              quickViewBtn +
            '</div>' +
            variantHtml +
            priceHtml +
          '</div>' +
          '<div class="paint-selection-drawer__item-actions">' +
            '<div class="paint-selection-drawer__qty">' +
              '<span class="paint-selection-drawer__qty-value">' + (item.quantity || 1) + '</span>' +
              '<button class="paint-selection-drawer__qty-btn" data-qty-decrease="' + index + '" aria-label="Decrease">' +
                '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
                '<path d="M15 18L9.5 12.5L15 7" stroke="#343642" stroke-width="1.375" stroke-linecap="round" stroke-linejoin="round"/>\n' +
                '</svg>' +
              '</button>' +
              '<button class="paint-selection-drawer__qty-btn" data-qty-increase="' + index + '" aria-label="Increase">' +
                '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
                '<path d="M9 7L14.5 12.5L9 18" stroke="#343642" stroke-width="1.375" stroke-linecap="round" stroke-linejoin="round"/>\n' +
                '</svg>' +
              '</button>' +
            '</div>' +
            '<button class="paint-selection-drawer__item-remove" data-remove-index="' + index + '" aria-label="Remove">' +
              '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M1 1L11 11M11 1L1 11" stroke="currentColor" stroke-width="1.5"/></svg>' +
            '</button>' +
          '</div>' +
        '</div>'
      );
    }).join('');
  }

  function openDrawer() {
    var drawer = document.getElementById('paint-selection-drawer');
    if (!drawer) return;
    renderDrawerItems(loadSelection());
    drawer.setAttribute('aria-hidden', 'false');
    document.body.setAttribute('data-drawer-modal-open', 'true');
  }

  function closeDrawer() {
    var drawer = document.getElementById('paint-selection-drawer');
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    document.body.setAttribute('data-drawer-modal-open', 'false');
  }

  async function addAllToCart() {
    var sel = loadSelection();
    if (sel.length === 0) return;

    var submitBtn = document.querySelector('.paint-selector__submit');
    if (submitBtn) submitBtn.disabled = true;

    try {
      var response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: sel.map(function (i) {
            return { id: parseInt(i.variantId, 10), quantity: i.quantity || 1 };
          })
        })
      });

      if (!response.ok) {
        var err = await response.json().catch(function () { return {}; });
        throw new Error(err.description || 'Failed to add to cart');
      }

      saveSelection([]);
      updateUI();
      closeDrawer();
      document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
    } catch (err) {
      console.error('[paint-selector] add to cart error:', err);
      if (submitBtn) {
        var orig = submitBtn.textContent;
        submitBtn.textContent = err.message || 'Something went wrong. Try again.';
        setTimeout(function () { if (submitBtn) submitBtn.textContent = orig; }, 3000);
      }
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  // ─── Events ───────────────────────────────────────────────────────────────
  // Note: the drawer lives outside the section element in the DOM, so all
  // drawer button events are delegated on document rather than section.

  var section = document.querySelector('[data-section-type="paint-selector"]');
  if (section) {
    section.addEventListener('click', function (e) {
      var addBtn = e.target.closest('.product-item__paint-selector-add .btn');
      if (addBtn) {
        var productItem = addBtn.closest('.product-item');
        if (productItem) addToSelection(productItem);
        return;
      }

      var decBtn = e.target.closest('[data-qty-decrease-product]');
      if (decBtn) {
        var decItem = decBtn.closest('.product-item');
        if (decItem) changeQuantityForProduct(decItem, -1);
        return;
      }

      var incBtn = e.target.closest('[data-qty-increase-product]');
      if (incBtn) {
        var incItem = incBtn.closest('.product-item');
        if (incItem) changeQuantityForProduct(incItem, 1);
      }
    });

    var viewBtn = document.querySelector('.paint-selector__view-selection');
    if (viewBtn) viewBtn.addEventListener('click', openDrawer);

    var submitBtn = document.querySelector('.paint-selector__submit');
    if (submitBtn) submitBtn.addEventListener('click', addAllToCart);

    var checkoutBtn = document.querySelector('.paint-selection-drawer__checkout');
    if (checkoutBtn) checkoutBtn.addEventListener('click', addAllToCart);
  }

  // Drawer interactions delegated on document (drawer is a sibling of section, not a child)
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-paint-drawer-close]') || e.target.closest('[data-paint-drawer-wash]')) {
      closeDrawer();
      return;
    }

    var removeBtn = e.target.closest('[data-remove-index]');
    if (removeBtn) {
      removeFromSelection(parseInt(removeBtn.dataset.removeIndex, 10));
      return;
    }

    var decBtn = e.target.closest('[data-qty-decrease]');
    if (decBtn) {
      changeQuantity(parseInt(decBtn.dataset.qtyDecrease, 10), -1);
      return;
    }

    var incBtn = e.target.closest('[data-qty-increase]');
    if (incBtn) {
      changeQuantity(parseInt(incBtn.dataset.qtyIncrease, 10), 1);
      return;
    }

    var qvBtn = e.target.closest('.paint-selection-drawer__quick-view');
    if (qvBtn && typeof window.emit === 'function') {
      window.emit('quick-view:open', null, {
        type:         'quick-view',
        productUrl:   qvBtn.dataset.url,
        productTitle: qvBtn.dataset.title,
        productMedia: null,
        productBadges: null
      });
      return;
    }

    if (e.target.closest('.paint-selector-sticky-bar__btn')) {
      addAllToCart();
    }
  });

  document.addEventListener('change', function (e) {
    var radio = e.target.closest('.paint-selection-drawer__variants input[type="radio"]');
    if (radio) changeVariant(parseInt(radio.dataset.index, 10), radio.value);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeDrawer();
  });

  updateUI();

  // Watch the submit button — show sticky bar only when it scrolls out of view
  if ('IntersectionObserver' in window) {
    var submitObserveTarget = document.querySelector('.paint-selector__submit');
    if (submitObserveTarget) {
      new IntersectionObserver(function (entries) {
        stickySubmitIsVisible = entries[0].isIntersecting;
        updateStickyBarVisibility();
      }, { threshold: 0 }).observe(submitObserveTarget);
    }
  }
})();


// Paint Selector — collection-scoped search
// Fetch this collection's products, then filter them locally. Shop-wide
// Storefront search is not collection-aware, and unpublished collections
// return no IDs, so the previous intersection was always empty.
(function () {
  var section = document.querySelector('[data-section-type="paint-selector"]');
  if (!section) return;

  var collectionHandle = section.dataset.collectionHandle;
  var shopDomain       = section.dataset.shopDomain;
  var storefrontToken  = section.dataset.storefrontToken;

  if (!collectionHandle) return;

  var PRODUCTS_PER_PAGE = 24;
  var SEARCH_DEBOUNCE_MS = 300;

  // ─── Session state ────────────────────────────────────────────────────────

  var originalListHTML   = null;
  var isSearchActive     = false;
  var activeFilters      = []; // [{ param, value }]
  var searchTimer        = null;

  var collectionProductsPromise = null;
  var COLLECTION_PRODUCTS_CACHE_KEY = 'paint-selector-products:' + collectionHandle;
  var COLLECTION_PRODUCTS_CACHE_TTL = 5 * 60 * 1000;

  var cachedQuery        = null;
  var cachedFilterKey    = null;
  var cachedResults      = [];
  var currentPage        = 0;

  // ─── Helpers ─────────────────────────────────────────────────────────────

  function escAttr(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function numericId(gid) {
    return String(gid).replace(/.*\//, '');
  }

  function formatMoney(amount) {
    return '£' + parseFloat(amount).toFixed(2);
  }

  function getList() {
    return document.querySelector('.paint-selector__products-list');
  }

  function filterKey() {
    return JSON.stringify(activeFilters);
  }

  function normalize(str) {
    return String(str || '').toLowerCase();
  }

  function sizedImage(url) {
    if (!url) return '';
    return url + (url.indexOf('?') === -1 ? '?width=450' : '&width=450');
  }

  // ─── Filter helpers ───────────────────────────────────────────────────────

  function paramToProductFilter(param, value) {
    var parts = param.replace(/^filter\./, '').split('.');
    if (parts[0] === 'p') {
      switch (parts[1]) {
        case 'tag':          return { tag: value };
        case 'vendor':       return { productVendor: value };
        case 'product_type': return { productType: value };
        case 'm':
          if (parts.length >= 4) {
            return { productMetafield: { namespace: parts[2], key: parts[3], value: value } };
          }
          break;
      }
    }
    if (parts[0] === 'v' && parts[1] === 'option' && parts.length >= 3) {
      return { variantOption: { name: parts[2], value: value } };
    }
    return null;
  }

  function productMatchesQuery(product, query) {
    var q = normalize(query);
    if (!q) return true;
    if (normalize(product.title).indexOf(q) !== -1) return true;
    if (normalize(product.vendor).indexOf(q) !== -1) return true;
    if (normalize(product.productType).indexOf(q) !== -1) return true;
    var tags = product.tags || [];
    for (var i = 0; i < tags.length; i++) {
      if (normalize(tags[i]).indexOf(q) !== -1) return true;
    }
    var edges = (product.variants && product.variants.edges) || [];
    for (var j = 0; j < edges.length; j++) {
      var variant = edges[j].node || {};
      if (normalize(variant.title).indexOf(q) !== -1) return true;
      if (normalize(variant.sku).indexOf(q) !== -1) return true;
    }
    return false;
  }

  function productMatchesFilters(product) {
    if (!activeFilters.length) return true;
    return activeFilters.every(function (filter) {
      var parsed = paramToProductFilter(filter.param, filter.value);
      if (!parsed) return productMatchesQuery(product, filter.value);
      if (parsed.tag) {
        return (product.tags || []).some(function (tag) {
          return normalize(tag) === normalize(parsed.tag);
        });
      }
      if (parsed.productVendor) return normalize(product.vendor) === normalize(parsed.productVendor);
      if (parsed.productType) return normalize(product.productType) === normalize(parsed.productType);
      if (parsed.variantOption) {
        var value = normalize(parsed.variantOption.value);
        var edges = (product.variants && product.variants.edges) || [];
        return edges.some(function (edge) {
          return normalize((edge.node || {}).title).indexOf(value) !== -1;
        });
      }
      // Metafield colour filters: colour name is typically in the product title.
      if (parsed.productMetafield) return productMatchesQuery(product, filter.value);
      return true;
    });
  }

  // ─── API ─────────────────────────────────────────────────────────────────

  function gqlFetch(query, variables) {
    return fetch('https://' + shopDomain + '/api/2024-01/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontToken,
      },
      body: JSON.stringify({ query: query, variables: variables }),
    }).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.json();
    }).then(function (data) {
      if (data.errors && data.errors.length) throw new Error(data.errors[0].message);
      return data.data;
    });
  }

  function fromAjaxProduct(product) {
    var image = product.featured_image || (product.images && product.images[0]) || null;
    var imageUrl = image && (image.src || image) ? sizedImage(image.src || image) : '';
    var variants = product.variants || [];
    var minPrice = variants.reduce(function (min, variant) {
      var amount = parseFloat(variant.price);
      return amount < min ? amount : min;
    }, Infinity);
    if (!isFinite(minPrice)) minPrice = 0;
    var tags = product.tags;
    if (typeof tags === 'string') {
      tags = tags.split(',').map(function (tag) { return tag.trim(); }).filter(Boolean);
    }
    return {
      id: 'gid://shopify/Product/' + product.id,
      title: product.title,
      handle: product.handle,
      vendor: product.vendor || '',
      productType: product.product_type || '',
      tags: tags || [],
      featuredImage: imageUrl ? { url: imageUrl, altText: product.title } : null,
      priceRange: { minVariantPrice: { amount: String(minPrice) } },
      variants: {
        edges: variants.map(function (variant) {
          return {
            node: {
              id: 'gid://shopify/ProductVariant/' + variant.id,
              title: variant.title || '',
              sku: variant.sku || '',
              availableForSale: !!variant.available,
              price: { amount: String(variant.price) },
              compareAtPrice: variant.compare_at_price ? { amount: String(variant.compare_at_price) } : null
            }
          };
        })
      }
    };
  }

  async function fetchViaAjax() {
    var products = [];
    var page = 1;
    while (page <= 20) {
      var response = await fetch(
        '/collections/' + encodeURIComponent(collectionHandle) + '/products.json?limit=250&page=' + page,
        { headers: { 'Accept': 'application/json' } }
      );
      if (!response.ok) throw new Error('HTTP ' + response.status);
      var data = await response.json();
      var batch = data.products || [];
      if (!batch.length) break;
      batch.forEach(function (product) { products.push(fromAjaxProduct(product)); });
      if (batch.length < 250) break;
      page++;
    }
    return products;
  }

  var GQL_COLLECTION_PRODUCTS = [
    'query CollectionProducts($handle: String!, $after: String) {',
    '  collection(handle: $handle) {',
    '    products(first: 50, after: $after) {',
    '      edges {',
    '        node {',
    '          id title handle vendor productType tags',
    '          featuredImage { url(transform: { maxWidth: 450 }) altText }',
    '          priceRange { minVariantPrice { amount } }',
    '          variants(first: 20) {',
    '            edges { node { id title sku availableForSale price { amount } compareAtPrice { amount } } }',
    '          }',
    '        }',
    '      }',
    '      pageInfo { hasNextPage endCursor }',
    '    }',
    '  }',
    '}',
  ].join('\n');

  async function fetchViaStorefront() {
    if (!storefrontToken || !shopDomain) return [];
    var products = [];
    var after = null;
    do {
      var data = await gqlFetch(GQL_COLLECTION_PRODUCTS, { handle: collectionHandle, after: after });
      var connection = data.collection && data.collection.products;
      if (!connection) break;
      connection.edges.forEach(function (edge) {
        if (edge.node && edge.node.id) products.push(edge.node);
      });
      after = connection.pageInfo.hasNextPage ? connection.pageInfo.endCursor : null;
    } while (after);
    return products;
  }

  function readCachedCollectionProducts() {
    try {
      var raw = sessionStorage.getItem(COLLECTION_PRODUCTS_CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.products) || (Date.now() - parsed.savedAt) > COLLECTION_PRODUCTS_CACHE_TTL) return null;
      return parsed.products;
    } catch (_) {
      return null;
    }
  }

  function writeCachedCollectionProducts(products) {
    try {
      sessionStorage.setItem(COLLECTION_PRODUCTS_CACHE_KEY, JSON.stringify({ products: products, savedAt: Date.now() }));
    } catch (_) {
      // sessionStorage unavailable/full — fine, cache is best-effort
    }
  }

  function ensureCollectionProducts() {
    if (collectionProductsPromise) return collectionProductsPromise;

    var cached = readCachedCollectionProducts();
    if (cached && cached.length) {
      collectionProductsPromise = Promise.resolve(cached);
      return collectionProductsPromise;
    }

    collectionProductsPromise = (async function () {
      var products = [];
      try {
        products = await fetchViaAjax();
      } catch (_) {
        products = [];
      }
      if (!products.length) {
        products = await fetchViaStorefront();
      }
      if (products.length) writeCachedCollectionProducts(products);
      return products;
    })();

    return collectionProductsPromise;
  }

  // ─── Rendering ───────────────────────────────────────────────────────────

  function renderProductCard(product) {
    var productId        = numericId(product.id);
    var variants         = product.variants.edges.map(function (e) { return e.node; });
    var defaultVariant   = variants[0] || {};
    var defaultVariantId = numericId(defaultVariant.id || '');
    var defaultPrice        = Math.round(parseFloat(((defaultVariant.price || {}).amount) || 0) * 100);
    var defaultCompareAt    = Math.round(parseFloat(((defaultVariant.compareAtPrice || {}).amount) || 0) * 100);
    var hasMultiple      = variants.length > 1;
    var imageUrl         = (product.featuredImage || {}).url || '';
    var imageAlt         = (product.featuredImage || {}).altText || product.title;
    var productPath      = '/collections/' + collectionHandle + '/products/' + product.handle;
    var priceLabel       = formatMoney(product.priceRange.minVariantPrice.amount);

    var variantHTML = '';
    if (hasMultiple) {
      variantHTML = '<div class="product-item__paint-selector__variants">' +
        variants.map(function (v, i) {
          var vId           = numericId(v.id);
          var vPrice        = Math.round(parseFloat(((v.price || {}).amount) || 0) * 100);
          var vCompareAt    = Math.round(parseFloat(((v.compareAtPrice || {}).amount) || 0) * 100);
          return (
            '<div>' +
            '<input type="radio"' +
            ' id="ps-' + productId + '-' + vId + '"' +
            ' name="paint-' + productId + '"' +
            ' value="' + vId + '"' +
            ' data-price="' + vPrice + '"' +
            ' data-compare-at-price="' + vCompareAt + '"' +
            ' data-title="' + escAttr(v.title) + '"' +
            (i === 0 ? ' checked' : '') + ' />' +
            '<label for="ps-' + productId + '-' + vId + '">' + v.title + '</label>' +
            '</div>'
          );
        }).join('') +
        '</div>';
    }

    return (
      '<div class="product-item animation--item"' +
      ' data-input-item' +
      ' data-id="' + productId + '"' +
      ' data-title="' + escAttr(product.title) + '"' +
      ' data-url="' + productPath + '"' +
      ' data-variant-id="' + defaultVariantId + '"' +
      ' data-featured-image="' + imageUrl + '">' +
        '<div class="product-item__inner">' +
          '<div class="product-item__media-wrapper">' +
            '<div class="product-item__media">' +
              '<a class="product-item__image-link" href="' + productPath + '" aria-label="' + escAttr(product.title) + '">' +
                '<div class="image product-item__image">' +
                  (imageUrl ? '<img src="' + imageUrl + '" alt="' + escAttr(imageAlt) + '" loading="lazy" style="width:100%;display:block;">' : '') +
                '</div>' +
              '</a>' +
            '</div>' +
          '</div>' +
          '<div class="product-item__meta">' +
            '<div class="product-item__text ta-l">' +
              '<div class="product-item__text_group_primary">' +
                '<div><h3 class="product-item__product-title fs-product-title ff-body"><a href="' + productPath + '">' + product.title + '</a></h3></div>' +
                '<div class="product-item__price-wrap"><p class="product-item__price ff-product-price"><span class="price">' + priceLabel + '</span></p></div>' +
              '</div>' +
              '<div class="product-item__paint-selector-add" data-quantity="0" data-default-price="' + defaultPrice + '" data-default-compare-at-price="' + defaultCompareAt + '">' +
                variantHTML +
                '<button type="button" class="btn product-item__paint-selector-add-btn">Add</button>' +
                '<div class="product-item__paint-selector-qty" data-paint-selector-qty>' +
                  '<button type="button" class="product-item__paint-selector-qty-btn" data-qty-decrease-product aria-label="Decrease quantity">' +
                    '<span class="icon icon-minus-small">' +
                    '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
                    '<path d="M1.5 10.75H.25v2.5H1.5v-2.5zm21 2.5h1.25v-2.5H22.5v2.5zm-21 0H12v-2.5H1.5v2.5zm10.5 0h10.5v-2.5H12v2.5z" fill="currentColor"/>' +
                    '</svg>' +
                    '</span>' +
                  '</button>' +
                  '<span class="product-item__paint-selector-qty-value" data-paint-selector-qty-value>0</span>' +
                  '<button type="button" class="product-item__paint-selector-qty-btn" data-qty-increase-product aria-label="Increase quantity">' +
                    '<span class="icon icon-plus-small">' +
                    '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
                    '<path d="M13.25 1.5V.25h-2.5V1.5h2.5zm-2.5 21v1.25h2.5V22.5h-2.5zM1.5 10.75H.25v2.5H1.5v-2.5zm21 2.5h1.25v-2.5H22.5v2.5zm-21 0H12v-2.5H1.5v2.5zm10.5 0h10.5v-2.5H12v2.5zM10.75 1.5V12h2.5V1.5h-2.5zm0 10.5v10.5h2.5V12h-2.5z" fill="currentColor"/>' +
                    '</svg>' +
                    '</span>' +
                  '</button>' +
                '</div>' +
              '</div>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</div>'
    );
  }

  function renderPage(page) {
    var list = getList();
    if (!list) return;

    if (cachedResults.length === 0) {
      list.innerHTML = '<p class="paint-selector__no-results">No products found in this collection for that search.</p>';
      return;
    }

    var totalPages = Math.ceil(cachedResults.length / PRODUCTS_PER_PAGE);
    var start      = page * PRODUCTS_PER_PAGE;
    var slice      = cachedResults.slice(start, start + PRODUCTS_PER_PAGE);

    var html = slice.map(renderProductCard).join('');

    if (totalPages > 1) {
      html += '<div class="paint-selector__search-pagination">';
      if (page > 0) {
        html += '<button class="btn btn--secondary paint-selector-pagination__prev" data-page="' + (page - 1) + '">← Previous</button>';
      }
      if (page < totalPages - 1) {
        html += '<button class="btn btn--secondary paint-selector-pagination__next" data-page="' + (page + 1) + '">Next →</button>';
      }
      html += '</div>';
    }

    list.innerHTML = html;
    if (page > 0) list.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  // ─── Search flow ─────────────────────────────────────────────────────────

  function setListLoading(loading) {
    var list = getList();
    if (!list) return;
    if (loading) { list.setAttribute('data-ps-loading', ''); }
    else         { list.removeAttribute('data-ps-loading'); }
  }

  function clearSearch() {
    isSearchActive  = false;
    cachedQuery     = null;
    cachedFilterKey = null;
    cachedResults   = [];
    currentPage     = 0;
    var list = getList();
    if (list && originalListHTML !== null) list.innerHTML = originalListHTML;
  }

  async function runSearch(textQuery, page) {
    textQuery = (textQuery || '').trim();
    page      = page || 0;

    if (!textQuery) { clearSearch(); return; }

    isSearchActive = true;

    var fKey = filterKey();
    if (textQuery !== cachedQuery || fKey !== cachedFilterKey) {
      setListLoading(true);
      try {
        var products = await ensureCollectionProducts();
        cachedResults = products.filter(function (product) {
          return productMatchesQuery(product, textQuery) && productMatchesFilters(product);
        });
        cachedQuery     = textQuery;
        cachedFilterKey = fKey;
        page            = 0;
      } catch (err) {
        console.error('[paint-selector] search error:', err);
        var list = getList();
        if (list) list.innerHTML = '<p class="paint-selector__no-results">Search failed — please try again.</p>';
        setListLoading(false);
        return;
      } finally {
        setListLoading(false);
      }
    }

    currentPage = page;
    renderPage(currentPage);
  }

  function scheduleSearch(value) {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(function () {
      runSearch(value);
    }, SEARCH_DEBOUNCE_MS);
  }

  // ─── Event wiring ─────────────────────────────────────────────────────────
  // The script tag uses `defer`, so the DOM is already fully parsed here.
  // No DOMContentLoaded wrapper needed.

  function getSearchInput() {
    return document.querySelector('.paint-selector__search input[type="search"]');
  }

  var list = getList();
  if (list) originalListHTML = list.innerHTML;

  // Seed active filters from URL (set by server-side filter navigation)
  var urlParams = new URLSearchParams(window.location.search);
  urlParams.forEach(function (val, key) {
    if (key.startsWith('filter.')) activeFilters.push({ param: key, value: val });
  });

  ensureCollectionProducts().catch(function () {});

  // ── Search input ──
  var searchWrap   = document.querySelector('.paint-selector__search');
  var searchInput  = getSearchInput();
  var searchButton = document.querySelector('.paint-selector__search button');

  if (searchWrap) {
    searchWrap.addEventListener('click', function (e) {
      e.stopPropagation();
    });
  }

  if (searchInput) {
    searchInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        clearTimeout(searchTimer);
        runSearch(searchInput.value);
      }
    });

    searchInput.addEventListener('input', function () {
      if (searchInput.value.trim() === '') {
        clearTimeout(searchTimer);
        if (isSearchActive) clearSearch();
        return;
      }
      scheduleSearch(searchInput.value);
    });
  }

  if (searchButton) {
    searchButton.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      clearTimeout(searchTimer);
      var input = getSearchInput();
      runSearch(input ? input.value : '');
    });
  }

  // ── Colour filter links ──
  var filtersContainer = document.querySelector('.paint-selector__filters');
  if (filtersContainer) {
    filtersContainer.addEventListener('click', function (e) {
      var link = e.target.closest('a[data-filter-param]');
      if (!link || !isSearchActive) return;
      e.preventDefault();

      var param = link.dataset.filterParam;
      var value = link.dataset.filterValue;

      var existingIdx = activeFilters.findIndex(function (f) {
        return f.param === param && f.value === value;
      });

      if (existingIdx >= 0) {
        activeFilters.splice(existingIdx, 1);
        link.classList.remove('active');
      } else {
        activeFilters.push({ param: param, value: value });
        link.classList.add('active');
      }

      var input = getSearchInput();
      runSearch(input ? input.value : '');
    });
  }

  // ── Pagination ──
  var productsRight = document.querySelector('.paint-selector__right');
  if (productsRight) {
    productsRight.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-page]');
      if (!btn) return;
      var input = getSearchInput();
      runSearch(input ? input.value : '', parseInt(btn.dataset.page, 10));
    });
  }


})();
